import React, { type FC, useEffect, useMemo, useState } from "react";
import "./WsInplantCard.scss";
import { useParams } from "react-router-dom";
import { useRequiredNumberParam } from "@src/hooks/useRequiredNumberParam";
import { useActionSheet, useWindows } from "@app/overlay";
import { API_USER_OPERATION_LIST, useBoAvOperations, useBoRights } from "@src/api/bo";
import { CardHeader } from "@shared/ui/card-header";
import type { ActionSheetSection } from "@shared/ui/overlay/action-sheet";
import { DetailList } from "@shared/ui/detail-row";
import {
    BusinessBoStateValue,
    BusinessDivisionValue,
    BusinessDateValue,
    formatBoStateText,
    formatDivisionText,
} from "@src/components/business-data-display";
import { checkBalanceDocType } from "@src/businessModules/WorkShop/shared/checkBalanceDocType";
import { BalanceDocType } from "@src/types/business/balanceDocType";
import { CardLoader } from "@src/components/card-loader";
import { useListMutate } from "@shared/hooks/useListMutate";
import { WsInplantConentList } from "./components/ContentList/WsInplantConentList";
import { wsInplantCardWindows } from "./wsInplantCard.windows";
import { useWsInplantCardMutations } from "./useWsInplantCardMutations";
import { useBoOperationConfirm } from "@src/businessModules/WorkShop/shared/bo/useBoOperationConfirm";
import { useWsInplantCardQuery } from "./hooks/useWsInplantCardQuery";
import { useWsInplantContentsQuery } from "./hooks/useWsInplantContentsQuery";
import { useBoOperationActionItems } from "@src/businessModules/WorkShop/shared/bo/useBoOperationActionItems";

export const WsInplantCard: FC = () => {
    const { docType: paramDocType } = useParams();
    const code = useRequiredNumberParam("code", "код ВЗН");
    const windows = useWindows();
    const actionSheet = useActionSheet();

    const cardQuery = useWsInplantCardQuery(code);
    const contentsQuery = useWsInplantContentsQuery(code);
    const contentMutate = useListMutate(contentsQuery, (content) => content.Code);
    const wsInplant = cardQuery.data;

    const { hasRight } = useBoRights({
        boCode: wsInplant?.Code,
        rightCodes: [API_USER_OPERATION_LIST["Редактирование объекта"]],
        enabled: wsInplant != null,
    });
    const readonly = !hasRight(API_USER_OPERATION_LIST["Редактирование объекта"]);

    const avOperationsQuery = useBoAvOperations(wsInplant?.Code, wsInplant != null);

    const [docType, setDocType] = useState<BalanceDocType>();
    const [showInfo, setShowInfo] = useState(true);

    const mutations = useWsInplantCardMutations({
        contentMutate,
        onBoOperationSuccess: () => {
            void cardQuery.refetch();
            void avOperationsQuery.refetch();
            void contentsQuery.refetch();
        },
    });

    const { confirmAndPerform } = useBoOperationConfirm({
        perform: mutations.performBoOperationMutation.mutateAsync,
    });

    const operationItems = useBoOperationActionItems({
        operations: avOperationsQuery.data,
        context: wsInplant,
        confirmAndPerform,
        buildParams: (operation, card) => {
            const status = formatBoStateText(card.Bo?.State, "short");

            return {
                operation,
                boCode: card.Code,
                documentLabel: `ВЗН №${card.Num}`,
                fields: [
                    { label: "Отправитель", value: formatDivisionText(card.Sender, card.SenderSection) },
                    { label: "Получатель", value: formatDivisionText(card.Receiver, card.ReceiverSection) },
                    { label: "Статус", value: status === "" ? "—" : status },
                ],
            };
        },
    });

    const openContentWindow = (params: { mode: "create" | "edit"; contentCode?: number }): void => {
        if (wsInplant == null || docType == null) {
            return;
        }

        wsInplantCardWindows.content.open(windows, {
            mode: params.mode,
            contentCode: params.contentCode,
            inplantCode: wsInplant.Code,
            inplantNum: wsInplant.Num,
            docType,
            readonly,
            senderDivisionCode: wsInplant.SenderCode,
            mutations,
        });
    };

    const actionSheetSections = useMemo<ActionSheetSection[]>(() => {
        const sections: ActionSheetSection[] = [
            {
                id: "view",
                items: [
                    {
                        type: "toggle",
                        id: "show-bo-info",
                        label: "Информация о БО",
                        description: "Отображать доп. информацию о БО",
                        checked: showInfo,
                        onCheckedChange: setShowInfo,
                    },
                ],
            },
        ];

        if (operationItems.length > 0) {
            sections.push({
                id: "bo",
                items: [
                    {
                        type: "drill-in",
                        id: "bo-operations",
                        label: "Выполнить операцию",
                        description: "Выбрать доступную BO-операцию",
                        sections: [{ id: "bo-operations", items: operationItems }],
                    },
                ],
            });
        }

        return sections;
    }, [operationItems, showInfo]);

    useEffect(() => {
        if (paramDocType == null || !checkBalanceDocType(paramDocType)) {
            throw new Error("Не подходящее значение типа списка Приход/Расход");
        }
        setDocType(paramDocType);
    }, [paramDocType]);

    if (docType == null) {
        return null;
    }

    if (cardQuery.isFetching || wsInplant == null) {
        return <CardLoader header list />;
    }

    return (
        <>
            <div className="ws_inplant_card">
                <div className="ws_inplant_card_header_container">
                    <CardHeader
                        text={`ВЗН №${wsInplant.Num} (${docType === BalanceDocType.Arrival ? "Приход" : "Расход"})`}
                        buttons={[
                            {
                                type: "icon",
                                text: "Действия",
                                srcIcon: "/images/common/more_vert.svg",
                                onClick: () => actionSheet.open({ title: "Действия", sections: actionSheetSections }),
                            },
                        ]}
                        className="ws_inplant_card_header"
                    />
                    {showInfo && (
                        <DetailList className="ws_inplant_data">
                            <DetailList.Row
                                label={"Отправитель"}
                                value={
                                    <BusinessDivisionValue division={wsInplant.Sender} section={wsInplant.SenderSection} />
                                }
                            />
                            <DetailList.Row
                                label={"Получатель"}
                                value={
                                    <BusinessDivisionValue
                                        division={wsInplant.Receiver}
                                        section={wsInplant.ReceiverSection}
                                    />
                                }
                            />
                            <DetailList.Row
                                label={"Статус"}
                                value={<BusinessBoStateValue state={wsInplant.Bo?.State} labelSource="short" />}
                            />
                            <DetailList.Row
                                label={"Дата выдачи"}
                                value={<BusinessDateValue value={wsInplant.LeaveMoveDate} />}
                            />
                        </DetailList>
                    )}
                </div>
                <WsInplantConentList
                    contentsQuery={contentsQuery}
                    docType={docType}
                    readonly={readonly}
                    onCreate={() => openContentWindow({ mode: "create" })}
                    onOpenContent={(contentCode) => openContentWindow({ mode: "edit", contentCode })}
                />
            </div>
        </>
    );
};
